// Hideo - state of "Drop your idea here" on the Home (src/components/IdeaDrop.vue): the typed title and text, the
// chosen image, the recorded voice note, the recording in progress and the "Added to the plan" message.
// It lives here, not in the component, so nothing is lost when the Home redraws or when the user goes to another page
// and comes back (a recording keeps going meanwhile and stops by itself after 3 minutes).
import { defineStore } from 'pinia'
import { reactive } from 'vue'
import { t } from '../i18n'
import { IDEA_AUDIO_MAX, REC_TYPES, VOICE_BITRATE, VOICE_MAX_SEC, ideaFileKind, ideaNotes, ideaReady, ideaTitle, ideaVideo } from '../features/idea'
import { useDataStore } from './data'
import { newItemProject } from './projects'
import { uiAlert, uiSuccess } from './notices'
import { saveAppData } from '../features/storage/save'
import { mediaPut } from '../features/cloud/media'
import { blobToDataUrl, readImg } from '../utils/image'
import { isWebImg } from '../utils/url'
import { imageFromLink } from '../features/images'
import { LIVE_BARS, audioContextClass, levelOf } from '../features/waveform'

/** The browser can record from the microphone */
export const canRecord = (): boolean => !!(navigator.mediaDevices && 'getUserMedia' in navigator.mediaDevices && window.MediaRecorder)

export const useIdeaStore = defineStore('idea', () => {
  const s = reactive({
    title: '', text: '', image: '', audio: '',
    /** Recording in progress (until the recorded audio is ready) and its seconds */
    recording: false, recSec: 0,
    /** Microphone level of the last moments of the recording (live waveform, newest last) */
    levels: [] as number[],
    sending: false,
    /** "Added to the plan" message (6 seconds): title and id of the new video */
    msg: null as { title: string; id: string } | null,
    /** The text field should take the focus (end of the guided tour) */
    wantFocus: false
  })
  let rec: MediaRecorder | null = null, timer = 0, msgTimer = 0, levelTimer = 0, listen: AudioContext | null = null

  const ready = () => ideaReady(s)
  function clearMedia(kind: 'image' | 'audio'): void {
    s[kind] = ''
  }

  // Voice note recording (max 3 minutes, voice quality so saves stay light)
  async function startRec(): Promise<void> {
    let stream: MediaStream
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true })
    } catch {
      uiAlert(t('Microphone not available: check that you gave the site permission to use the microphone.'))
      return
    }
    const mime = REC_TYPES.find(type => MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(type))
    const r = new MediaRecorder(stream, { ...(mime ? { mimeType: mime } : {}), audioBitsPerSecond: VOICE_BITRATE }), chunks: Blob[] = []
    r.ondataavailable = e => { if (e.data && e.data.size) chunks.push(e.data) }
    r.onstop = async () => {
      stream.getTracks().forEach(track => track.stop())
      clearInterval(timer)
      stopLevels()
      s.audio = chunks.length ? await blobToDataUrl(new Blob(chunks, { type: r.mimeType || 'audio/webm' })) : ''
      rec = null
      s.recording = false
    }
    rec = r
    s.audio = ''
    startLevels(stream)
    r.start()
    const start = Date.now()
    s.recording = true
    s.recSec = 0
    timer = window.setInterval(() => {
      const sec = Math.floor((Date.now() - start) / 1000)
      s.recSec = sec
      if (sec >= VOICE_MAX_SEC) stopRec()
    }, 250)
  }
  // Live waveform: the microphone's level ten times a second (nothing when the browser has no Web Audio)
  function startLevels(stream: MediaStream): void {
    s.levels = []
    const AC = audioContextClass()
    if (!AC) return
    try {
      listen = new AC()
      const an = listen.createAnalyser()
      an.fftSize = 1024
      listen.createMediaStreamSource(stream).connect(an)
      const buf = new Uint8Array(an.fftSize)
      levelTimer = window.setInterval(() => {
        an.getByteTimeDomainData(buf)
        s.levels.push(levelOf(buf))
        if (s.levels.length > LIVE_BARS) s.levels.shift()
      }, 100)
    } catch {
      stopLevels()
    }
  }
  function stopLevels(): void {
    clearInterval(levelTimer)
    if (listen) void listen.close().catch(() => {})
    listen = null
  }
  function stopRec(): void {
    if (rec && rec.state !== 'inactive') rec.stop()
  }

  // Chosen or dropped file: image (downscaled like the others) or audio (max 3 MB)
  async function pickFile(f: File | null | undefined): Promise<void> {
    if (!f) return
    const kind = ideaFileKind(f)
    if (kind === 'image') s.image = await readImg(f, 1280)
    else if (kind === 'audio') {
      if (f.size > IDEA_AUDIO_MAX) return uiAlert(t('Audio file too large (3 MB max): record a shorter voice note.'))
      if (rec) stopRec()
      s.audio = await blobToDataUrl(f)
    } else uiAlert(t('Here you can add a text, an audio file or an image.'))
  }
  /** An image from a link: a copy like a chosen file when the site lets the app read it, otherwise the address itself */
  async function pickLink(url: string): Promise<void> {
    if (!isWebImg(url)) return uiAlert(t('Paste the address of an image, starting with https://'))
    const v = await imageFromLink(url, b => readImg(b, 1280))
    if (v) s.image = v
  }
  /** Something dropped on the box: a file (image or audio), otherwise its text (added to the idea) */
  function dropped(dt: DataTransfer | null): void {
    const f = dt && dt.files && dt.files[0]
    if (f) {
      pickFile(f)
      return
    }
    const text = ((dt && dt.getData('text/plain')) || '').trim()
    if (text) s.text = s.text.trim() ? s.text.trimEnd() + '\n' + text : text
  }

  /** Creates the video in the plan (Idea column); image and voice note are stored apart (src/features/cloud/media.ts) */
  async function submit(): Promise<void> {
    if (!ready() || s.sending) return
    const title = ideaTitle(s), notes = ideaNotes(s)
    s.sending = true
    let image = '', voiceNote = ''
    try {
      // an image kept as a web address stays a link (like the video dialog's image box)
      if (s.image) image = isWebImg(s.image) ? s.image.trim() : await mediaPut(s.image, 'img')
      if (s.audio) voiceNote = await mediaPut(s.audio, 'voice')
    } finally {
      s.sending = false
    }
    const d = useDataStore().getData(), id = 'v_' + Date.now()
    d.videos.push(ideaVideo(d, { id, title, notes, projectId: newItemProject(), image, voiceNote }))
    saveAppData(d)
    Object.assign(s, { text: '', title: '', image: '', audio: '' })
    uiSuccess(t('Idea added to the plan'))
    s.msg = { title, id }
    clearTimeout(msgTimer)
    msgTimer = window.setTimeout(() => { s.msg = null }, 6000)
  }
  /** Puts the cursor in the text field (end of the guided tour; on a computer) */
  function focusText(): void {
    s.wantFocus = true
  }
  return { s, ready, clearMedia, startRec, stopRec, pickFile, pickLink, dropped, submit, focusText }
})
